import React, { useState, useRef, forwardRef, useImperativeHandle } from 'react';

const OLLAMA_URL = `${window.location.protocol}//ollama.techni.co.kr/api/generate`;

const AI_MODELS = [
  'bnksys/eeve:10.8b-korean-instruct-q5_k_s-v1',
  'bnksys/yanolja-eeve-korean-instruct-10.8b:latest',
  'qwen2.5:7b',
  'qwen2.5-coder:7b',
  'translategemma:4b',
  'hf.co/solang/Midm-2.0-Base-Instruct-Q4_K_M-GGUF:latest',
];

const PREFIX_MAP = {
  en: '아래 글을 영어로 번역해줘. 번역 결과 텍스트만 출력하고 설명이나 옵션은 출력하지 마. ',
  ko: '아래 글을 한글로 번역해줘. 번역 결과 텍스트만 출력하고 설명이나 옵션은 출력하지 마. ',
  zh: '아래 글을 중국어로 번역해줘. 번역 결과 텍스트만 출력하고 설명이나 옵션은 출력하지 마. ',
  ja: '아래 글을 일본어로 번역해줘. 번역 결과 텍스트만 출력하고 설명이나 옵션은 출력하지 마. ',
};
const LABEL_MAP = { en: '영문번역', ko: '한글번역', zh: '중문번역', ja: '일어번역' };

export { AI_MODELS };

const EXTRACTABLE_EXTS = new Set(['.txt', '.csv', '.html', '.htm', '.pdf', '.docx', '.xlsx', '.xls']);

function isExtractable(file) {
  const ext = ('.' + (file.originalName || '').split('.').pop()).toLowerCase();
  return EXTRACTABLE_EXTS.has(ext);
}

/**
 * Props:
 *   selectedFiles        {Array}    처리할 파일 목록 — FileAttachment의 선택 파일 또는 전체 파일
 *   fileTextApiBase      {string}   파일 텍스트 추출 API 기본 경로 (기본값: '/api/board/file')
 *   model                {string}   사용할 AI 모델 (부모에서 관리, 콤보박스 선택값)
 *   fixedSummarizeModel  {string}   요약에 사용할 모델 고정 (선택)
 *   fixedTranslateModel  {string}   번역에 사용할 모델 고정 (선택)
 *                                   둘 다 지정 시 콤보박스 숨김, 하나만 지정 시 콤보박스 표시
 *   onLoadingChange      {Function} (isLoading: boolean) => void — 부모에 로딩 상태 전달
 *
 * Ref (forwardRef):
 *   runSummarize()
 *   runTranslate(lang)   // 'en'|'ko'|'zh'|'ja'
 *   handleStop()
 */
const AiFileToolbar = forwardRef(function AiFileToolbar(
  { selectedFiles = [], fileTextApiBase = '/api/board/file', model, fixedSummarizeModel, fixedTranslateModel, onLoadingChange },
  ref
) {
  const [aiLabel, setAiLabel] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const aiAbortRef = useRef(null);
  const [results, setResults] = useState([]);
  const [copied, setCopied] = useState(false);

  const summarizeModel = fixedSummarizeModel || model || AI_MODELS[0];
  const translateModel = fixedTranslateModel || model || AI_MODELS[0];

  const setLoading = (val) => {
    setAiLoading(val);
    onLoadingChange?.(val);
  };

  /* ── 공통 스트리밍 ── */
  const streamToState = async (prompt, model, onChunk, controller) => {
    const res = await fetch(OLLAMA_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ model, prompt, stream: true }),
      signal: controller.signal,
    });
    if (!res.ok) throw new Error(`HTTP 오류: ${res.status}`);
    const reader = res.body.getReader();
    const decoder = new TextDecoder('utf-8');
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      const chunk = decoder.decode(value, { stream: true });
      for (const line of chunk.split('\n').filter(l => l.trim())) {
        try {
          const parsed = JSON.parse(line);
          if (parsed.response) onChunk(parsed.response);
        } catch (_) {}
      }
    }
  };

  /* ── AI 실행 (파일별 순차 처리) ── */
  const runAi = async (buildPrompt, label, model) => {
    const filesToProcess = selectedFiles.filter(isExtractable);
    if (filesToProcess.length === 0) return;

    const controller = new AbortController();
    aiAbortRef.current = controller;
    setLoading(true);
    setResults([]);
    setAiLabel(label);

    for (const file of filesToProcess) {
      let fileText = '';
      try {
        const res = await fetch(`${fileTextApiBase}/${file.id}/text`);
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.message || `HTTP ${res.status}`);
        }
        const data = await res.json();
        fileText = data.text;
      } catch (err) {
        if (err.name === 'AbortError') break;
        setResults(prev => [...prev, {
          id: file.id, originalName: file.originalName, text: '', error: `텍스트 추출 실패: ${err.message}`
        }]);
        continue;
      }

      setResults(prev => [...prev, { id: file.id, originalName: file.originalName, text: '' }]);
      try {
        await streamToState(buildPrompt(fileText), model, (chunk) => {
          setResults(prev => {
            const next = [...prev];
            const idx = next.findIndex(r => r.id === file.id);
            if (idx >= 0) next[idx] = { ...next[idx], text: next[idx].text + chunk };
            return next;
          });
        }, controller);
      } catch (err) {
        if (err.name === 'AbortError') break;
        setResults(prev => {
          const next = [...prev];
          const idx = next.findIndex(r => r.id === file.id);
          if (idx >= 0) next[idx] = { ...next[idx], error: `AI 오류: ${err.message}` };
          return next;
        });
      }
    }

    setLoading(false);
    aiAbortRef.current = null;
  };

  const runSummarize = () => runAi((text) => `아래 글을 요약해줘.\n${text}`, '파일 요약', summarizeModel);
  const runTranslate = (lang) => runAi((text) => `${PREFIX_MAP[lang]}\n${text}`, `파일 ${LABEL_MAP[lang]}`, translateModel);
  const handleStop = () => { if (aiAbortRef.current) aiAbortRef.current.abort(); };

  useImperativeHandle(ref, () => ({ runSummarize, runTranslate, handleStop }));

  const handleCopy = () => {
    const text = results
      .map(r => results.length > 1 ? `[${r.originalName}]\n${r.text}` : r.text)
      .join('\n\n---\n\n');
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div>
      {(results.length > 0 || aiLoading) && (
        <div className="mt-2 p-3 border rounded bg-light">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <small className="fw-bold text-warning">AI {aiLabel}</small>
            {!aiLoading && (
              <div className="d-flex align-items-center gap-2">
                <button className="btn btn-link btn-sm p-0 text-muted" onClick={handleCopy}>
                  {copied ? '복사됨 ✓' : '복사'}
                </button>
                <button className="btn btn-link btn-sm p-0 text-muted" onClick={() => setResults([])}>
                  닫기
                </button>
              </div>
            )}
          </div>

          {results.length === 0 && aiLoading && (
            <div style={{ fontSize: '0.9rem', color: '#888' }}>생성 중...</div>
          )}

          {results.map((r, i) => (
            <div key={r.id}>
              {results.length > 1 && (
                <div className="text-secondary fw-semibold mb-1 mt-2"
                  style={{ fontSize: '0.8rem', borderBottom: '1px solid #dee2e6', paddingBottom: 4 }}>
                  📎 {r.originalName}
                </div>
              )}
              {r.error ? (
                <div className="text-danger" style={{ fontSize: '0.85rem' }}>{r.error}</div>
              ) : (
                <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, fontSize: '0.9rem' }}>
                  {r.text || (aiLoading && i === results.length - 1 ? '생성 중...' : '')}
                </div>
              )}
              {i < results.length - 1 && <hr className="my-3" />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

export default AiFileToolbar;
